import { endOfYear, formatDate, isCurrentYear, startOfYear } from '~/utils/date';
import { ClubSearchSelect } from '~/pages/club/components/club-search-select';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { useAuthentication } from '~/hooks/useAuthentication';
import { Schema as StdSchema } from './license-form.schema';
import { Select } from '~/components/Input/Select/Select';
import { schema, Schema } from './license-year.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { AxiosResponse, isAxiosError } from 'axios';
import { Panel } from '~/components/Panel/Panel';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useNavigate } from 'react-router';
import { Resolver, useForm } from 'react-hook-form';
import { LicenseWrite } from '~/apis/ssf';

interface Props {
  onSubmit: (data: StdSchema) => Promise<AxiosResponse<LicenseWrite, Error>>;
  defaultValues?: Partial<StdSchema>;
  mode: 'create' | 'edit';
  isLoading: boolean;
}

function extractYear(date: string | undefined): number {
  const fallback = new Date().getFullYear();

  if (!date) {
    return fallback;
  }

  return new Date(date).getFullYear();
}

function getSelectableYears() {
  const now = new Date();
  const years = [now.getFullYear()];

  /**
   * Only include next year from November forwards.
   * Months are 0 based... So 10 = Nov.
   */
  if (now.getMonth() >= 10) {
    years.push(now.getFullYear() + 1);
  }

  return years;
}

export function LicenseYearForm({ defaultValues, mode, isLoading, onSubmit }: Props) {
  const { isSuperUser } = useAuthentication();
  const navigate = useNavigate();

  const { formState, register, handleSubmit, setValue, setError, watch, trigger } = useForm<Schema>({
    defaultValues: {
      year: extractYear(defaultValues?.start_date),
      club: defaultValues?.club,
    },
    resolver: zodResolver(schema) as unknown as Resolver<Schema>,
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  async function trySubmit(payload: Schema) {
    try {
      /**
       * If it's the current year, use today's date.
       * If it's the next year, use 1st of Jan.
       */
      const start = isCurrentYear(payload.year) ? new Date() : startOfYear(payload.year);

      /**
       * If it's the current year, use end of current year.
       * If it's the next year, use end of next year.
       */
      const end = isCurrentYear(payload.year) ? endOfYear() : endOfYear(payload.year);

      const { data } = await onSubmit({
        club: payload.club,
        start_date: start.toLocaleString('sv-SE').split(' ')[0],
        end_date: end.toLocaleString('sv-SE').split(' ')[0],
      });

      navigate(`/lifters/${data.person}/licenses`);
    } catch (error) {
      if (isAxiosError(error) && error.status === 400) {
        setError('year', { message: 'Personen har redan en licens för det angivna året' });
      } else {
        throw error;
      }
    }
  }

  const selectableYears = getSelectableYears();

  return (
    <form onSubmit={handleSubmit(trySubmit)} autoComplete="off">
      <Grid>
        {selectableYears.length <= 1 && (
          <Grid.Item cols={{ sm: 12, lg: 12 }}>
            <Panel variant="informative" size="sm">
              Licenser för {new Date().getFullYear() + 1} kan skapas från och med{' '}
              {formatDate(new Date(new Date().getFullYear(), 10, 1), 'yyyy-mm-dd')}
            </Panel>
          </Grid.Item>
        )}

        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group formState={formState} htmlFor="club" label="Förening">
            <ClubSearchSelect
              placeholder="Sök på förening..."
              disabled={!isSuperUser}
              formState={formState}
              value={watch('club')}
              name="club"
              onChange={v => {
                setValue('club', v as [string, number], { shouldDirty: true });
                trigger('club');
              }}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group formState={formState} htmlFor="year" label="År">
            <Select
              options={selectableYears.map(year => [year.toString(), year.toString()])}
              {...register('year')}
              id="year"
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Button.Group align="right">
        <Button type="submit" isLoading={isLoading}>
          {{ create: 'Skapa', edit: 'Spara' }[mode]}
        </Button>
      </Button.Group>

      {dialog}
    </form>
  );
}
